import type { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from './stores/auth';
import { me, type ClientMe } from './api/portal';
import {
  hasClips,
  hasHall,
  hasLine,
  hasOffice,
  hasOnAir,
  hasScouting,
  hasSocial,
  hasWire,
  isLeagueLogin,
  portalHomePath,
} from './lib/portalRole';
import { LoginPage } from './pages/LoginPage';
import { HandoffPage } from './pages/HandoffPage';
import { MatchesPage } from './pages/MatchesPage';
import { KeysPage } from './pages/KeysPage';
import { WebhooksPage } from './pages/WebhooksPage';
import { SitesPage } from './pages/SitesPage';
import { UsagePage } from './pages/UsagePage';
import { SetupPage } from './pages/SetupPage';
import { ClubsPage } from './pages/ClubsPage';
import { MatchReportPage } from './pages/MatchReportPage';
import { RoundsPage } from './pages/RoundsPage';
import { ClipsPage } from './pages/ClipsPage';
import { GeneratedVideosPage } from './pages/GeneratedVideosPage';
import BrandStudioPage from './pages/BrandStudioPage';
import { ClubHomePage } from './pages/ClubHomePage';
import { SocialDeskPage } from './pages/SocialDeskPage';
import { SponsorPage } from './pages/SponsorPage';
import { PlayerCardsPage } from './pages/PlayerCardsPage';
import { PressPage } from './pages/PressPage';
import { ScoutingPage } from './pages/ScoutingPage';
import { DisciplinePage } from './pages/DisciplinePage';
import { VenuesPage } from './pages/VenuesPage';
import { HistoryPage } from './pages/HistoryPage';
import { WirePage } from './pages/WirePage';
import { OnAirPage } from './pages/OnAirPage';
import { LinePage } from './pages/LinePage';
import { Layout } from './layout/Layout';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5_000, retry: 1, refetchOnWindowFocus: false } },
});

function RequireAuth() {
  const token = useAuthStore((s) => s.accessToken);
  if (!token) return <Navigate to="/login" replace />;
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function HomeRedirect() {
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  if (!profile.data) return null;
  return <Navigate to={portalHomePath(profile.data)} replace />;
}

function PackRoute({
  allow,
  children,
}: {
  allow: (profile: ClientMe) => boolean;
  children: ReactNode;
}) {
  const profile = useQuery({ queryKey: ['portal-me'], queryFn: me });
  if (!profile.data) return null;
  if (!allow(profile.data)) {
    return <Navigate to={portalHomePath(profile.data)} replace />;
  }
  return children;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/handoff" element={<HandoffPage />} />
          <Route element={<RequireAuth />}>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/home" element={<ClubHomePage />} />
            <Route path="/matches" element={<MatchesPage />} />
            <Route path="/matches/:id/report" element={<MatchReportPage />} />
            <Route path="/rounds" element={<PackRoute allow={(p) => isLeagueLogin(p) && hasOffice(p)}><RoundsPage /></PackRoute>} />
            <Route path="/rounds/:key" element={<PackRoute allow={(p) => isLeagueLogin(p) && hasOffice(p)}><RoundsPage /></PackRoute>} />
            <Route path="/clips" element={<PackRoute allow={hasClips}><ClipsPage /></PackRoute>} />
            <Route path="/videos" element={<PackRoute allow={hasClips}><GeneratedVideosPage /></PackRoute>} />
            <Route path="/studio" element={<PackRoute allow={hasClips}><BrandStudioPage /></PackRoute>} />
            <Route path="/social" element={<PackRoute allow={hasSocial}><SocialDeskPage /></PackRoute>} />
            <Route path="/sponsor" element={<PackRoute allow={(p) => isLeagueLogin(p) && hasHall(p)}><SponsorPage /></PackRoute>} />
            <Route path="/cards" element={<PackRoute allow={isLeagueLogin}><PlayerCardsPage /></PackRoute>} />
            <Route path="/press" element={<PackRoute allow={(p) => isLeagueLogin(p) && hasOffice(p)}><PressPage /></PackRoute>} />
            <Route path="/scouting" element={<PackRoute allow={hasScouting}><ScoutingPage /></PackRoute>} />
            <Route path="/discipline" element={<PackRoute allow={(p) => isLeagueLogin(p) && hasOffice(p)}><DisciplinePage /></PackRoute>} />
            <Route path="/venues" element={<PackRoute allow={(p) => isLeagueLogin(p) && hasHall(p)}><VenuesPage /></PackRoute>} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/wire" element={<PackRoute allow={hasWire}><WirePage /></PackRoute>} />
            <Route path="/onair" element={<PackRoute allow={hasOnAir}><OnAirPage /></PackRoute>} />
            <Route path="/line" element={<PackRoute allow={hasLine}><LinePage /></PackRoute>} />
            <Route path="/keys" element={<KeysPage />} />
            <Route path="/webhooks" element={<WebhooksPage />} />
            <Route path="/sites" element={<SitesPage />} />
            <Route path="/usage" element={<UsagePage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/clubs" element={<ClubsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
